import _ from 'lodash';

/** A calendar date with no time component, e.g. 2026-09-23. */
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parses a date string in the format YYYY-MM-DD into a Date object
 *
 * This will correctly cast the date into the browser's timezone unlike
 * the Date constructor which will assume the date is in UTC
 *
 * @param str The date string to parse
 * @returns The parsed date
 * @throws Error if the string is not in the correct format
 * @example
 */
export function parseIsoDate(str: string) {
  if (!str) {
    throw new Error('No date provided');
  }

  if (!DATE_ONLY_PATTERN.test(str)) {
    throw new Error('Invalid date format must be YYYY-MM-DD');
  }

  // Parse the string into a date
  const [year, month, day] = str.split('-').map((n) => parseInt(n));
  return new Date(year, month - 1, day);
}

/**
 * Returns true if the date is today
 *
 * @param someDate The date to check
 * @returns True if the date is today
 */
export function isToday(someDate: Date, timezone: string): boolean {
  const today = new Date();
  return isSameDay(someDate, today, timezone);
}

export function isYesterday(someDate: Date, timezone: string): boolean {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return isSameDay(someDate, yesterday, timezone);
}

function isSameDay(someDate: Date, targetDate: Date, timezone: string): boolean {
  if (!someDate) {
    return false;
  }
  const day = new Date(someDate);
  const options: Intl.DateTimeFormatOptions = {
    month: '2-digit',
    day: '2-digit',
    timeZone: timezone
  };

  return (
    day.toLocaleString([], {
      month: '2-digit',
      day: '2-digit',
      timeZone: 'UTC'
    }) === targetDate.toLocaleString([], options)
  );
}

/**
 * Computes the age of a person given their birth date
 * @param birthDate The person's birth date
 * @returns The person's age in years
 */
export function getAge(birthDate: Date): number {
  const today = new Date();
  return getAgeAsOf(birthDate, today);
}

export function getAgeAsOf(birthDate: Date, today: Date): number {
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();

  // Adjust the age if the current month is before the birth month, or if the current month is the same but the current day is before the birth day
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  if (age < 0) {
    return 0;
  }

  return age;
}

/**
 * Formats an ISO date string into a user friendly readable date
 * @param isoString The ISO date string (ex. 2023-06-30T15:18:50.176Z)
 * @returns a formatted date ('Jun 30, 2023, 11:18:50 AM')
 */
export function formatISOStringToReadableDate(isoString: string) {
  return new Date(isoString).toLocaleString([], { dateStyle: 'medium', timeStyle: 'medium' });
}

/**
 * Get the number of days between two dates
 * @param startDate a date in the past of endDate
 * @param endDate a date in the future of startDate (default: current date)
 * @param allowZero if true, returns 0 if less than one full day has passed (default: false)
 * @returns the number of days between the dates
 */
export function daysBetween(startDate: Date, endDate = new Date(), allowZero = false): string {
  if (startDate > endDate) {
    return '0';
  }

  // Calculate the time difference in milliseconds
  const timeDiff = endDate.getTime() - startDate.getTime();

  // Convert the time difference to days
  const daysDiff = timeDiff / (1000 * 60 * 60 * 24);

  // Apply logic based on allowZero flag
  return (allowZero ? Math.floor(daysDiff) : Math.ceil(daysDiff)).toString();
}

/**
 * Get the number of minutes between two dates
 * @param startDate a date in the past of endDate
 * @param endDate a date in the future of startDate
 * @returns the number of minutes between the dates
 */
export function minutesBetween(startDate: Date, endDate = new Date()): number {
  if (startDate > endDate) {
    return 0;
  }

  // Calculate the time difference in milliseconds
  const timeDiff = Math.abs(endDate.getTime() - startDate.getTime());

  // Convert the time difference to minutes
  return Math.ceil(timeDiff / (1000 * 60));
}

/**
 * Get a Date object from a date string with an appended time
 *
 * Ex. "2021-09-01" -> "2021-09-01T00:00"
 *
 * Prevents incorrect date parsing due to timezone differences
 * @param dateString a date string in the format "YYYY-MM-DD"
 * @returns a Date object
 */
export function dateFromDateStringWithAppendedTime(dateString: string) {
  const date = new Date(`${dateString}T00:00`);

  return _.isDate(date) && new Date(date).toString() !== 'Invalid Date'
    ? date
    : new Date(dateString);
}

/**
 * Converts a Date object to an ISO string in the format YYYY-MM-DDTHH:mm:ssZ
 * @param date The Date object to convert
 * @returns The ISO string representation of the date
 */
export function toIsoString(date: Date): string {
  const tzo = -date.getTimezoneOffset();
  const dif = tzo >= 0 ? '+' : '-';
  const pad = (num: number) => (num < 10 ? '0' : '') + num;

  return (
    date.getFullYear() +
    '-' +
    pad(date.getMonth() + 1) +
    '-' +
    pad(date.getDate()) +
    'T' +
    pad(date.getHours()) +
    ':' +
    pad(date.getMinutes()) +
    ':' +
    pad(date.getSeconds()) +
    dif +
    pad(Math.floor(Math.abs(tzo) / 60)) +
    ':' +
    pad(Math.abs(tzo) % 60)
  );
}

/**
 * Returns today's date as a YYYY-MM-DD string in the browser's local timezone.
 *
 * Uses explicit field extraction rather than toLocaleDateString() to avoid
 * locale-dependent output formats (e.g. en-US returns "6/19/2026", not
 * "2026-06-19") and inconsistent behaviour across environments.
 */
export function todayLocalDateString(): string {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Returns the closest upcoming Monday (today, if today is itself a Monday) as
 * a YYYY-MM-DD string.
 *
 * @param from Reference date (defaults to now)
 * @param timeZone IANA timezone to compute "today" in (e.g. the member's time
 * zone). Defaults to the browser's local timezone.
 */
export function getClosestUpcomingMonday(from = new Date(), timeZone?: string): string {
  const reference = timeZone ? replaceTimezone(from, timeZone) : from;
  const daysUntilMonday = (1 - reference.getDay() + 7) % 7;
  const monday = new Date(reference);
  monday.setDate(reference.getDate() + daysUntilMonday);

  const year = monday.getFullYear();
  const month = String(monday.getMonth() + 1).padStart(2, '0');
  const day = String(monday.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Returns the next strictly-future Monday as a YYYY-MM-DD string, in the
 * browser's local timezone.
 *
 * Always at least a week out from `from`'s own week, even when `from` is
 * itself a Monday -- callers use this to schedule a change for "next week"
 * rather than "this week."
 *
 * @param from Reference date (defaults to now)
 */
export function getNextMonday(from = new Date()): string {
  const daysSinceMonday = (from.getDay() + 6) % 7;
  const thisWeeksMonday = new Date(from);
  thisWeeksMonday.setDate(from.getDate() - daysSinceMonday);
  thisWeeksMonday.setDate(thisWeeksMonday.getDate() + 7);

  const year = thisWeeksMonday.getFullYear();
  const month = String(thisWeeksMonday.getMonth() + 1).padStart(2, '0');
  const day = String(thisWeeksMonday.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Returns the most recent Monday on or before `from` as a YYYY-MM-DD string,
 * in the browser's local timezone. Unlike getNextMonday, this can fall in the
 * past -- callers use it to allow starting from the current week rather than
 * forcing a jump to next week.
 *
 * @param from Reference date (defaults to now)
 */
export function getThisWeeksMonday(from = new Date()): string {
  const daysSinceMonday = (from.getDay() + 6) % 7;
  const monday = new Date(from);
  monday.setDate(from.getDate() - daysSinceMonday);

  const year = monday.getFullYear();
  const month = String(monday.getMonth() + 1).padStart(2, '0');
  const day = String(monday.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Returns the browser/runtime's own IANA timezone name.
 */
export function getBrowserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Whether `timeZone` is an IANA timezone name the runtime recognizes.
 */
export function isValidTimeZone(timeZone: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Returns the calendar date (YYYY-MM-DD) an ISO instant falls on within the
 * given IANA timezone -- unlike a bare `new Date(dateString)`, this respects
 * timeZone rather than the runtime's own zone.
 *
 * @param dateString ISO datetime string
 * @param timeZone IANA timezone name (e.g. "US/Eastern")
 */
export function dateStringInZone(dateString: string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(new Date(dateString));
  const byType = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return `${byType.year}-${byType.month}-${byType.day}`;
}

/**
 * Adds (or subtracts, for a negative count) whole days to a YYYY-MM-DD date
 * string, returning a new YYYY-MM-DD string. Pure calendar arithmetic -- no
 * timezone is involved, since a bare date has none.
 *
 * @param dateStr Date string in YYYY-MM-DD format
 * @param days Number of days to add (negative to subtract)
 */
export function addDaysToDateString(dateStr: string, days: number): string {
  const date = parseIsoDate(dateStr);
  date.setDate(date.getDate() + days);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Converts the time of a date to that of the targetTimeZone
 * @param date The date to convert
 * @param targetTimeZone The target timezone
 */
export const replaceTimezone = (date: Date, targetTimeZone: string): Date => {
  const timeZone = getBrowserTimeZone();
  const thisNow = new Date(new Date().toLocaleString('en-US', { timeZone: timeZone }));
  const dstNow = new Date(thisNow.toLocaleString('en-US', { timeZone: targetTimeZone }));
  const delta = thisNow.getTime() - dstNow.getTime();

  return new Date(date.getTime() + delta);
};

/**
 * Formats an ISO date string as a short two-digit date (MM/DD/YY).
 *
 * Both month and day are zero-padded to two digits and the year is abbreviated
 * to two digits.
 *
 * @param dateString ISO date string (e.g. '2026-03-05T12:00:00Z')
 * @returns Formatted date string (e.g. '03/05/26')
 */
export function formatShortDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit'
  });
}

/**
 * Formats an ISO date string as a short two-digit date with 12-hour time (MM/DD/YY, h:mm AM/PM).
 *
 * Useful for displaying creation timestamps in a compact format.
 *
 * @param dateString ISO date string (e.g. '2026-03-05T14:30:00Z')
 * @returns Formatted date-time string (e.g. '03/05/26, 2:30 PM')
 */
export function formatShortDateTime(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Formats an ISO date string as a month and day (e.g. 'Apr 26').
 *
 * @param dateString ISO date string (e.g. '2026-04-26T14:00:00Z')
 * @returns Formatted date string (e.g. 'Apr 26')
 */
export function formatMonthDay(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Returns the number of days from a given reference point (default: now) until a future date.
 * Uses ceiling so a partial day counts as a full day.
 *
 * @param dateString ISO date string of the target date
 * @param from Reference date (defaults to current time)
 * @returns Number of days until the target date (may be negative if in the past)
 */
export function daysUntil(dateString: string, from = new Date()): number {
  const target = new Date(dateString);
  const diffMs = target.getTime() - from.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * Returns the number of days until a date, clamped to a minimum of 0.
 * If the date is in the past, returns 0 instead of a negative number.
 *
 * @param dateString ISO date string of the target date
 * @param from Reference date (defaults to current time)
 * @returns Number of days until the target date, minimum 0
 */
export function daysUntilMin0(dateString: string, from = new Date()): number {
  return Math.max(0, daysUntil(dateString, from));
}

/**
 * Formats an ISO date string as a month and day, or "Today" if the date is in the past.
 *
 * @param dateString ISO date string (e.g. '2026-04-26T14:00:00Z')
 * @returns Formatted date string (e.g. 'Apr 26') or 'Today' if in the past
 */
export function formatMonthDayOrToday(dateString: string): string {
  const daysAway = daysUntil(dateString);
  return daysAway < 0 ? 'Today' : formatMonthDay(dateString);
}

/**
 * Formats a YYYY-MM-DD date string as a human-readable calendar date (e.g. "May 29, 2026").
 *
 * @param dateStr Date string in YYYY-MM-DD format
 * @returns Formatted date string (e.g. "May 29, 2026")
 */
export function formatCalendarDate(dateStr: string): string {
  return parseIsoDate(dateStr).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}

/**
 * Formats a YYYY-MM-DD date string as a human-readable calendar date, or an
 * em dash when the date is null/undefined (e.g. an open-ended end date).
 *
 * @param dateStr Date string in YYYY-MM-DD format, or null/undefined
 * @returns Formatted date string (e.g. "May 29, 2026") or "—"
 */
export function formatOptionalCalendarDate(dateStr?: string | null): string {
  return dateStr ? formatCalendarDate(dateStr) : '—';
}

export function formatNumericCalendarDate(dateStr: string): string {
  return parseIsoDate(dateStr.split('T')[0]).toLocaleDateString('en-US');
}

/**
 * Formats a 24-hour "HH:mm:ss" time string as a 12-hour time (e.g. "2:30 PM").
 *
 * @param time Time string in HH:mm:ss format
 * @returns Formatted time string (e.g. "2:30 PM")
 */
export function formatTimeOfDay(time: string): string {
  const [hoursStr, minutesStr] = time.split(':');
  const hours = Number(hoursStr);
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const displayHours = ((hours + 11) % 12) + 1;
  return `${displayHours}:${minutesStr} ${suffix}`;
}

/**
 * Formats a real ISO datetime string as a 12-hour time of day in the given
 * IANA timezone (e.g. "10:00 AM"). Unlike formatTimeOfDay, which formats a
 * naive local time string, this converts an actual instant into a timezone.
 *
 * @param dateString ISO datetime string
 * @param timeZone IANA timezone name (e.g. "US/Eastern")
 */
export function formatTimeOfDayInZone(dateString: string, timeZone: string): string {
  return new Date(dateString).toLocaleTimeString('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Abbreviates an IANA timezone name for display next to a naive local time
 * (e.g. "America/New_York" -> "EDT"/"EST" depending on the date), so a
 * viewer in a different zone isn't left guessing which timezone a bare time
 * like "9:00 AM" is in.
 *
 * @param timeZone IANA timezone name (e.g. "America/New_York")
 * @param date Instant to resolve the abbreviation as of (defaults to now,
 * since standard/daylight abbreviations vary by date)
 */
export function getTimeZoneAbbreviation(timeZone: string, date: Date = new Date()): string {
  if (!timeZone) return '';

  try {
    const part = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short'
    })
      .formatToParts(date)
      .find((formatted) => formatted.type === 'timeZoneName');
    return part?.value ?? '';
  } catch {
    return '';
  }
}

/**
 * Formats an ISO datetime string as a short weekday + month/day (e.g.
 * "Thu, Jul 30"). Unlike formatShortDateTime, this has no year or time of
 * day component -- for occurrence list headings where the date needs a
 * weekday, not a full timestamp.
 *
 * @param dateString ISO datetime string
 * @param timeZone Optional IANA timezone name to render the date in
 */
export function formatWeekdayDate(dateString: string, timeZone?: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    timeZone,
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Parses a Healthie date string in the format "YYYY-MM-DD HH:mm:ss Z" into a Date object
 * @param date The date string to parse
 * @returns The parsed Date object or null if invalid
 */
export const parseHealthieDate = (date: string | undefined | null) => {
  if (!date) {
    return null;
  }

  // Replace space before timezone with empty string to match ISO format
  // "2024-11-04 14:30:00 -0500" -> "2024-11-04T14:30:00-0500"
  const isoFormat = date.replace(' ', 'T').replace(' ', '');
  const parsedDate = new Date(isoFormat);

  if (isNaN(parsedDate.getTime())) {
    console.error('Invalid date format:', date);
    return null;
  }

  return parsedDate;
};

/**
 * Formats an appointment date the way Healthie's rel_appt_date expects: YYYYMMDD.
 *
 * A date-only string is already the calendar date we mean, so it is used
 * verbatim. Reading it through Date would treat it as UTC midnight and shift it
 * a day for anyone west of Greenwich.
 * @param appointmentDate A Date, an ISO timestamp, or a YYYY-MM-DD string
 * @returns The date as YYYYMMDD
 */
export const healthieApptDate = (appointmentDate: Date | string): string => {
  if (typeof appointmentDate === 'string' && DATE_ONLY_PATTERN.test(appointmentDate)) {
    return appointmentDate.replace(/-/g, '');
  }

  const date = typeof appointmentDate === 'string' ? new Date(appointmentDate) : appointmentDate;

  return date.toISOString().slice(0, 10).replace(/-/g, '');
};
